import { useState } from "react";
import type { CharacterState } from "@/data/types";
import { calcDerivedStats } from "@/lib/characterCalc";
import { getValidationIssues } from "@/lib/characterValidation";
import {
  applyRulesUpgrade,
  planRulesUpgrade,
  type RulesUpgradePlan,
} from "@/lib/rulesMigration";
import { checkUltimateEligibility } from "@/lib/ultimateWarlock";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowUpCircle,
  ChevronLeft,
  ChevronRight,
  Minus,
  Plus,
} from "lucide-react";

export function RulesUpgradeBanner({
  state,
  onChange,
}: {
  state: CharacterState;
  onChange: (s: CharacterState) => void;
}) {
  const [open, setOpen] = useState(false);
  const plan = planRulesUpgrade(state);
  if (!plan) return null;

  return (
    <div className="rounded-lg border border-primary/50 bg-primary/10 px-4 py-3 mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
      <ArrowUpCircle className="h-5 w-5 shrink-0 text-primary" />
      <p className="text-sm flex-1">
        This character uses <strong>{plan.fromRules.metadata.label}</strong>{" "}
        rules. Walk through the changes needed to make it{" "}
        {plan.toRules.metadata.label} compatible.
      </p>
      <Button size="sm" onClick={() => setOpen(true)}>
        Upgrade to {plan.toRules.metadata.label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        {open && (
          <RulesUpgradeWalkthrough
            state={state}
            plan={plan}
            onComplete={(upgraded) => {
              onChange(upgraded);
              setOpen(false);
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

function RulesUpgradeWalkthrough({
  state,
  plan,
  onComplete,
}: {
  state: CharacterState;
  plan: RulesUpgradePlan;
  onComplete: (s: CharacterState) => void;
}) {
  const { fromRules, toRules, overCapSpells, untiedMissionGifts } = plan;
  // Over-cap spells only exist when a cap applies.
  const levelCap = plan.maxSpellLevel ?? Infinity;
  const steps = [
    "changes",
    ...(overCapSpells.length > 0 ? ["spells" as const] : []),
    ...(untiedMissionGifts.length > 0 ? ["missionGifts" as const] : []),
    "confirm",
  ] as const;
  const [stepIdx, setStepIdx] = useState(0);
  const step = steps[stepIdx];
  const [missionGiftPatrons, setMissionGiftPatrons] = useState<
    Record<number, string>
  >({});

  // Spell redistribution draft: over-cap spells start clamped to the cap.
  const original: Record<string, number> = Object.fromEntries(
    state.spellAllocations.map((a) => [a.spellId, a.points])
  );
  const [draft, setDraft] = useState<Record<string, number>>(() => ({
    ...original,
    ...Object.fromEntries(overCapSpells.map((s) => [s.spellId, levelCap])),
  }));
  const overCapIds = new Set(overCapSpells.map((s) => s.spellId));
  const excessByList: Record<string, number> = {};
  for (const s of overCapSpells)
    excessByList[s.listId] =
      (excessByList[s.listId] ?? 0) + s.points - levelCap;

  const upgraded = applyRulesUpgrade(state, plan, {
    spellAllocations: Object.entries(draft).map(([spellId, points]) => ({
      spellId,
      points,
    })),
    missionGiftPatrons,
  });
  const beforeStats = calcDerivedStats(state, fromRules);
  const afterStats = calcDerivedStats(upgraded, toRules);
  const remainingIssues = getValidationIssues(upgraded, afterStats, toRules);
  const ultimateStatus = (s: CharacterState, r: typeof toRules) => {
    if (s.ultimateWarlock) return "Ascended";
    const { metCount, totalCount, eligible } = checkUltimateEligibility(s, r);
    return eligible
      ? "Eligible to ascend"
      : `${metCount}/${totalCount} requirements`;
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Upgrade to {toRules.metadata.label} rules</DialogTitle>
        <DialogDescription>
          Step {stepIdx + 1} of {steps.length}
        </DialogDescription>
      </DialogHeader>

      {step === "changes" && (
        <div className="space-y-4 text-sm">
          <div>
            <h3 className="font-semibold mb-1">Changes to your character</h3>
            {plan.notices.length === 0 ? (
              <p className="text-muted-foreground">
                Nothing on your sheet changes automatically.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {plan.notices.map((n, i) => (
                  <li key={i}>
                    <span className="font-medium">{n.title}:</span>{" "}
                    <span className="text-muted-foreground">{n.detail}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <h3 className="font-semibold mb-1">
              What&apos;s new in {toRules.metadata.label}
            </h3>
            <ul className="space-y-1 list-disc pl-5 text-muted-foreground">
              {plan.headlines.map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {step === "spells" && (
        <div className="space-y-4 text-sm">
          <p className="text-muted-foreground">
            Spells now max out at level {levelCap}. Points above the cap may be
            redistributed into other spells from the same group. Any points you
            don&apos;t place are left unspent.
          </p>
          {Object.keys(excessByList).map((listId) => {
            const list = toRules.spellLists.find((l) => l.id === listId);
            if (!list) return null;
            const targets = list.spells.filter(
              (s) => !s.deprecated && !overCapIds.has(s.id)
            );
            const placed = targets.reduce(
              (sum, s) => sum + (draft[s.id] ?? 0) - (original[s.id] ?? 0),
              0
            );
            const unplaced = (excessByList[listId] ?? 0) - placed;
            return (
              <div key={listId} className="border rounded-md p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">{list.name}</h3>
                  <Badge variant={unplaced > 0 ? "warning" : "secondary"}>
                    {unplaced} to place
                  </Badge>
                </div>
                {overCapSpells
                  .filter((s) => s.listId === listId)
                  .map((s) => (
                    <div
                      key={s.spellId}
                      className="flex justify-between text-muted-foreground"
                    >
                      <span>
                        {list.spells.find((sp) => sp.id === s.spellId)?.name}
                      </span>
                      <span>
                        level {s.points} → {levelCap}
                      </span>
                    </div>
                  ))}
                {targets.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No other spells in this group can take these points.
                  </p>
                )}
                {targets.map((spell) => {
                  const pts = draft[spell.id] ?? 0;
                  const cap = spell.scaling === "binary" ? 1 : levelCap;
                  return (
                    <div
                      key={spell.id}
                      className="flex items-center justify-between gap-3"
                    >
                      <span>{spell.name}</span>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="icon-sm"
                          disabled={pts <= (original[spell.id] ?? 0)}
                          onClick={() =>
                            setDraft({ ...draft, [spell.id]: pts - 1 })
                          }
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-6 text-center font-medium">
                          {pts}
                        </span>
                        <Button
                          variant="outline"
                          size="icon-sm"
                          disabled={unplaced <= 0 || pts >= cap}
                          onClick={() =>
                            setDraft({ ...draft, [spell.id]: pts + 1 })
                          }
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {step === "missionGifts" && (
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            Mission reward Gifts can now be tied to the Patron who granted them,
            so they count towards holding a Gift from each Patron (Ultimate
            Warlock). These rewards have no Patron listed — choose one, or None
            if the reward wasn&apos;t from a Patron.
          </p>
          {untiedMissionGifts.map((idx) => {
            const sg = state.standaloneGifts?.[idx];
            const gift = toRules.otherworldlyGifts.find(
              (g) => g.id === sg?.giftId
            );
            return (
              <div
                key={idx}
                className="flex items-center justify-between gap-3 border rounded-md p-2"
              >
                <span className="font-medium">{gift?.name ?? sg?.giftId}</span>
                <select
                  className="border rounded-md bg-background px-2 py-1 text-sm"
                  value={missionGiftPatrons[idx] ?? ""}
                  onChange={(e) =>
                    setMissionGiftPatrons({
                      ...missionGiftPatrons,
                      [idx]: e.target.value,
                    })
                  }
                >
                  <option value="">None</option>
                  {toRules.patrons.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.exiled ? " (Previous Patron)" : ""}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      )}

      {step === "confirm" && (
        <div className="space-y-4 text-sm">
          <table className="w-full">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="font-medium py-1"></th>
                <th className="font-medium py-1">{fromRules.metadata.label}</th>
                <th className="font-medium py-1">{toRules.metadata.label}</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Max HP", beforeStats.maxHp, afterStats.maxHp],
                ["MP", beforeStats.mp, afterStats.mp],
                [
                  "Wealth",
                  fromRules.wealthLevels[beforeStats.wealthLevel]?.name,
                  toRules.wealthLevels[afterStats.wealthLevel]?.name,
                ],
                [
                  "Ultimate Warlock",
                  ultimateStatus(state, fromRules),
                  ultimateStatus(upgraded, toRules),
                ],
              ].map(([label, from, to]) => (
                <tr key={label} className="border-t">
                  <td className="py-1 font-medium">{label}</td>
                  <td className="py-1">{from}</td>
                  <td className="py-1">{to}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {remainingIssues.length > 0 && (
            <div>
              <h3 className="font-semibold mb-1">
                Still to resolve after upgrading
              </h3>
              <ul className="space-y-0.5 list-disc pl-5 text-muted-foreground">
                {remainingIssues.map((issue, i) => (
                  <li key={i}>{issue.message}</li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-muted-foreground">
            Upgrading is one-way: your share link will switch to{" "}
            {toRules.metadata.label} rules. Keep a copy of your current link if
            you need the old version.
          </p>
        </div>
      )}

      <DialogFooter className="gap-2">
        {stepIdx > 0 && (
          <Button variant="outline" onClick={() => setStepIdx(stepIdx - 1)}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
        )}
        {step === "confirm" ? (
          <Button onClick={() => onComplete(upgraded)}>
            Upgrade character
          </Button>
        ) : (
          <Button onClick={() => setStepIdx(stepIdx + 1)}>
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        )}
      </DialogFooter>
    </DialogContent>
  );
}
